import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PageShellComponent } from '@shared/components/page-shell/page-shell.component';
import { EditorialSelectComponent } from '@features/nuvem/editorial-select.component';
import { PromoBannerComponent } from '@features/nuvem/promo-banner.component';
import { RelatorioService } from './relatorio.service';
import { RelatorioResponse } from './models/relatorio.models';
import {
  CATEGORY_ORDER,
  Locale,
  categoryCover,
  categoryDescription,
  categoryLabel,
  distinctTemas,
  formatMonthYear,
  temaLabel,
} from './evex.util';

interface CategoryGroup {
  key: string;
  label: string;
  description: string;
  cover: string;
  items: RelatorioResponse[];
}

@Component({
  selector: 'app-evidencia-express-page',
  standalone: true,
  imports: [
    TranslocoPipe,
    PageShellComponent,
    EditorialSelectComponent,
    PromoBannerComponent,
  ],
  templateUrl: './evidencia-express.page.html',
})
export class EvidenciaExpressPage {
  private readonly service = inject(RelatorioService);
  private readonly transloco = inject(TranslocoService);

  protected readonly breadcrumb = [{ labelKey: 'nav.items.evidenceExpress' }];

  private readonly relatorios = toSignal(this.service.getRelatorios(), {
    initialValue: [] as RelatorioResponse[],
  });
  protected readonly loading = computed(() => this.relatorios().length === 0);

  private readonly langSignal = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });
  private readonly locale = computed<Locale>(() =>
    this.langSignal() === 'en' ? 'en' : 'pt-BR',
  );

  protected readonly query = signal('');
  protected readonly categoria = signal<string>('all');
  protected readonly tema = signal<string>('all');

  /** Opções do filtro de categoria (só as presentes nos dados). */
  protected readonly categoriaOptions = computed(() => {
    const loc = this.locale();
    const present = new Set(this.relatorios().map((r) => r.categoria?.nome));
    const options = CATEGORY_ORDER.filter((k) => present.has(k)).map((k) => ({
      value: k,
      label: categoryLabel(k, loc),
    }));
    return [
      {
        value: 'all',
        label: this.transloco.translate('pages.evidenceExpress.filter.category'),
      },
      ...options,
    ];
  });

  /** Opções do filtro de tema (só os presentes nos dados). */
  protected readonly temaOptions = computed(() => {
    const loc = this.locale();
    const options = distinctTemas(this.relatorios(), loc).map((k) => ({
      value: k,
      label: temaLabel(k, loc),
    }));
    return [
      {
        value: 'all',
        label: this.transloco.translate('pages.evidenceExpress.filter.theme'),
      },
      ...options,
    ];
  });

  private readonly filtered = computed(() => {
    const q = this.query().trim().toLowerCase();
    const cat = this.categoria();
    const tema = this.tema();
    return this.relatorios().filter((r) => {
      if (cat !== 'all' && r.categoria?.nome !== cat) return false;
      if (tema !== 'all' && r.tema?.nome !== tema) return false;
      if (q && !r.titulo?.toLowerCase().includes(q)) return false;
      return true;
    });
  });

  protected readonly total = computed(() => this.filtered().length);

  /** Publicações agrupadas por categoria, na ordem de exibição. */
  protected readonly groups = computed<CategoryGroup[]>(() => {
    const loc = this.locale();
    const byCategory = new Map<string, RelatorioResponse[]>();
    for (const r of this.filtered()) {
      const key = r.categoria?.nome;
      if (!key) continue;
      (byCategory.get(key) ?? byCategory.set(key, []).get(key)!).push(r);
    }
    const keys = [
      ...CATEGORY_ORDER.filter((k) => byCategory.has(k)),
      ...[...byCategory.keys()].filter((k) => !CATEGORY_ORDER.includes(k)),
    ];
    return keys.map((key) => ({
      key,
      label: categoryLabel(key, loc),
      description: categoryDescription(key, loc),
      cover: categoryCover(key),
      items: byCategory.get(key) ?? [],
    }));
  });

  protected temaChip(key: string): string {
    return temaLabel(key, this.locale());
  }

  protected monthYear(pub: RelatorioResponse): string {
    return formatMonthYear(pub.data_publicacao, pub.ano_publicacao, this.locale());
  }

  protected onSearch(value: string): void {
    this.query.set(value);
  }
}
